// Çoklu müşteri seçici (2026-10-07) — `ClientPicker` ile aynı arama kutusu + listbox; seçim
// listeyi KAPATMAZ (işaretle/kaldır), seçilenler alanın altında çip (✕) + "Tümünü temizle".
// `mode="local"` + `options` ile küçük yerel listeler (Arşiv "Sektör") sunucuya gitmez.
// `fetchOptions` ile çağıran kendi kaynağını verebilir (Arşiv "Müşteri": boş sorguda
// tamamlanmış projesi olan ilk 50 + toplam, yazınca `GET /clients?search=`).

import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  CLIENT_PICKER_COPY,
  buildPickerRows,
  fillCopy,
  isPickerNavKey,
  moveActive,
  pushRecentClient,
  readRecentClients,
  selectableRowIndexes,
  type ClientPickerItem,
} from "../../lib/client-picker";
import { useMediaQuery } from "../../lib/use-media-query";
import { useAuthStore } from "../../stores/auth-store";
import {
  ChipRemoveButton,
  ClientPickerListbox,
  chipStyle,
  chipTextStyle,
  fieldStyle,
  inputStyle,
  isInsidePickerList,
  optionDomId,
  useClientPickerSearch,
  type ClientPickerSize,
  type ClientPickerSourceProps,
} from "./ClientPickerCore";

export interface ClientMultiPickerProps extends ClientPickerSourceProps {
  values: readonly ClientPickerItem[];
  onChange: (values: ClientPickerItem[]) => void;
  ariaLabel: string;
  id?: string | undefined;
  placeholder?: string | undefined;
  size?: ClientPickerSize | undefined;
  inline?: boolean | undefined;
  disabled?: boolean | undefined;
  /** Son kullanılanlar bölümü — yerel modda (sektör gibi) anlamsız, varsayılan sunucu modunda açık. */
  showRecent?: boolean | undefined;
}

export function ClientMultiPicker(props: ClientMultiPickerProps): JSX.Element {
  const {
    values,
    onChange,
    ariaLabel,
    id,
    placeholder = CLIENT_PICKER_COPY.placeholder,
    size = "md",
    inline = false,
    disabled = false,
    showRecent = (props.mode ?? "server") === "server",
    allLink = null,
    ...source
  } = props;
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const touch = useMediaQuery("(max-width: 768px)");
  const genId = useId();
  const inputId = id ?? `client-multi-${genId}`;
  const listId = `${inputId}-list`;
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [activeRow, setActiveRow] = useState(-1);
  const [recent, setRecent] = useState<ClientPickerItem[]>([]);

  useEffect(() => {
    if (open && showRecent) setRecent(readRecentClients(userId));
  }, [open, showRecent, userId]);

  const search = useClientPickerSearch(source, query, open);
  const rows = useMemo(
    () =>
      buildPickerRows({
        query,
        recent: showRecent ? recent : [],
        results: search.items,
        total: search.total,
      }),
    [query, recent, showRecent, search.items, search.total],
  );
  const selectable = useMemo(() => selectableRowIndexes(rows), [rows]);
  const selectedIds = useMemo(() => new Set(values.map((v) => v.id)), [values]);

  useEffect(() => {
    setActiveRow((a) => (a >= 0 && rows[a]?.kind === "option" ? a : -1));
  }, [rows]);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent): void {
      if (isInsidePickerList(e.target, listId)) return;
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  function close(): void {
    setOpen(false);
    setActiveRow(-1);
  }

  function toggle(item: ClientPickerItem): void {
    if (selectedIds.has(item.id)) onChange(values.filter((v) => v.id !== item.id));
    else {
      onChange([...values, { id: item.id, name: item.name }]);
      if (showRecent) setRecent(pushRecentClient(userId, item));
    }
    inputRef.current?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (disabled) return;
    if (isPickerNavKey(e.key)) {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      const pos = selectable.indexOf(activeRow);
      const next = moveActive(pos, e.key, selectable.length);
      setActiveRow(next < 0 ? -1 : selectable[next]!);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (!open) setOpen(true);
      else {
        const row =
          activeRow >= 0
            ? rows[activeRow]
            : selectable.length === 1
              ? rows[selectable[0]!]
              : undefined;
        if (row?.kind === "option") toggle(row.item);
      }
      return;
    }
    if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        close();
      } else if (query) {
        e.preventDefault();
        setQuery("");
      }
      return;
    }
    if (e.key === "Backspace" && query.length === 0 && values.length > 0) {
      e.preventDefault();
      onChange(values.slice(0, -1));
      return;
    }
    if (e.key === "Tab") close();
  }

  const activeId = open && activeRow >= 0 ? optionDomId(listId, activeRow) : undefined;

  return (
    <div ref={rootRef} style={{ position: "relative", minWidth: 0 }}>
      <div
        style={fieldStyle({ size, inline, focused, touch, disabled })}
        onClick={() => {
          if (disabled) return;
          inputRef.current?.focus();
          setOpen(true);
        }}
      >
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          aria-label={ariaLabel}
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listId}
          aria-activedescendant={activeId}
          aria-haspopup="listbox"
          autoComplete="off"
          disabled={disabled}
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActiveRow(-1);
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={onKeyDown}
          style={inputStyle(size)}
        />
        {values.length > 0 ? (
          <span
            style={{ fontSize: "0.72rem", color: "var(--color-text-3)", flexShrink: 0 }}
            aria-hidden="true"
          >
            {fillCopy(CLIENT_PICKER_COPY.selectedCount, { n: values.length })}
          </span>
        ) : null}
        <span
          aria-hidden="true"
          style={{
            color: "var(--color-text-3)",
            fontSize: "0.7rem",
            padding: "0 2px",
            flexShrink: 0,
          }}
        >
          ▾
        </span>
      </div>
      {open && !disabled ? (
        <ClientPickerListbox
          id={listId}
          anchorRef={rootRef}
          label={ariaLabel}
          rows={rows}
          activeRow={activeRow}
          selectedIds={selectedIds}
          query={query}
          loading={search.loading}
          error={search.error}
          onRetry={search.retry}
          onPick={toggle}
          allLink={allLink}
          touch={touch}
          size={size}
          multi
        />
      ) : null}
      {values.length > 0 ? (
        <div
          role="group"
          aria-label={`${ariaLabel} — ${fillCopy(CLIENT_PICKER_COPY.selectedCount, { n: values.length })}`}
          style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6, alignItems: "center" }}
        >
          {values.map((v) => (
            <span key={v.id} style={chipStyle(size)} title={v.name}>
              <span style={chipTextStyle}>{v.name}</span>
              <ChipRemoveButton
                label={fillCopy(CLIENT_PICKER_COPY.remove, { name: v.name })}
                onClick={() => onChange(values.filter((x) => x.id !== v.id))}
                touch={touch}
              />
            </span>
          ))}
          {values.length > 1 ? (
            <button
              type="button"
              onClick={() => onChange([])}
              style={{
                border: 0,
                background: "transparent",
                color: "var(--color-primary)",
                fontSize: "0.75rem",
                cursor: "pointer",
                padding: "2px 4px",
                minHeight: touch ? 44 : undefined,
              }}
            >
              {CLIENT_PICKER_COPY.clearAll}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
